<template>
  <div class="ste">
    <!-- Header -->
    <div class="ste-header">
      <div class="ste-title-row">
        <router-link :to="{ name: 'sms-templates-list' }" class="ste-back">
          <i class="material-icons">arrow_back</i>
        </router-link>
        <h2 class="ste-title">{{ isEdit ? 'Edit SMS Template' : 'New SMS Template' }}</h2>
      </div>
      <div class="ste-fields-row">
        <div class="ste-fields">
          <div class="ste-field">
            <label class="ste-label">Name</label>
            <input v-model="form.name" class="ste-input" placeholder="Untitled template" />
          </div>
        </div>
        <div class="ste-actions">
          <button class="ste-btn ste-btn--primary" @click="saveTemplate" :disabled="isSaving">
            <i class="material-icons" style="font-size:16px;">save</i>
            {{ isSaving ? 'Saving...' : 'Save Template' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Template picker (only on create) -->
    <SMSTemplatePicker
      v-if="!isEdit && !hasStarted"
      @select-template="onSelectTemplate"
      @select-blank="onSelectBlank"
    />

    <!-- Split-screen body -->
    <div v-else class="ste-split">
      <!-- Left: controls -->
      <div class="ste-edit">
        <div class="ste-form">
          <div class="form-group">
            <label class="ste-label">Variables</label>
            <div>
              <button
                v-for="v in variables"
                :key="v.value"
                type="button"
                class="ste-var-btn"
                @click="insertVariable(v.value)"
              >{{ v.label }}</button>
            </div>
            <small class="text-muted">Tap to insert at end of message.</small>
          </div>

          <div class="form-group mt-3">
            <label class="ste-label">Message Body</label>
            <textarea
              v-model="form.body"
              class="ste-input ste-textarea"
              rows="9"
              placeholder="Type your SMS here. Use {{FIRST_NAME}} etc. to personalize."
            />
            <div class="d-flex justify-content-between mt-1">
              <small class="text-muted">{{ charCount }} characters</small>
              <small class="text-muted">{{ segmentCount }} SMS segment{{ segmentCount === 1 ? '' : 's' }}</small>
            </div>
          </div>

          <div v-if="!isEdit" class="mt-3">
            <button type="button" class="ste-btn ste-btn--ghost" @click="hasStarted = false">
              &larr; Back to templates
            </button>
          </div>
        </div>
      </div>

      <!-- Right: live phone preview -->
      <div class="ste-preview">
        <div class="phone">
          <div class="phone-notch"></div>
          <div class="phone-header">
            <div class="phone-from">{{ fromLabel }}</div>
            <div class="phone-time">now</div>
          </div>
          <div class="phone-body">
            <div v-if="rendered" class="bubble inbound">{{ rendered }}</div>
            <div v-else class="bubble inbound bubble-empty">(No message body yet)</div>
          </div>
          <div class="phone-meta">
            Sample variables shown — actual recipient values are filled at send time.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { TEMPLATE_VARIABLES } from '../../helpers/email-variables';
import SMSTemplatePicker from '../../components/SMS/SMSTemplatePicker.vue';

const SAMPLE_VARS = {
  '{{FIRST_NAME}}': 'Jamie',
  '{{LAST_NAME}}': 'Rivera',
  '{{FULL_NAME}}': 'Jamie Rivera',
  '{{EMAIL}}': 'jamie@example.com',
  '{{PHONE}}': '(555) 123-4567',
  '{{SCHOOL_NAME}}': '',
  '{{CLASS_START_DATE}}': 'Monday, November 9th',
};

export default {
  name: 'SMSTemplateEditor',
  components: { SMSTemplatePicker },
  data() {
    return {
      form: { name: '', body: '', variables: [] },
      isEdit: false,
      isSaving: false,
      hasStarted: false,
      variables: TEMPLATE_VARIABLES,
    };
  },
  computed: {
    schoolName() {
      const user = this.$store.getters['auth/getLoggedInUser'];
      return (user && user.school && user.school.name) || 'Your School';
    },
    fromLabel() {
      const user = this.$store.getters['auth/getLoggedInUser'];
      const phone = user && user.school && user.school.twilio_phone_number;
      return phone || this.schoolName;
    },
    rendered() {
      let out = this.form.body || '';
      const vars = { ...SAMPLE_VARS, '{{SCHOOL_NAME}}': this.schoolName };
      Object.keys(vars).forEach(k => {
        out = out.split(k).join(vars[k]);
      });
      return out;
    },
    charCount() {
      return this.rendered.length;
    },
    segmentCount() {
      const len = this.charCount;
      if (len === 0) return 0;
      return len <= 160 ? 1 : Math.ceil(len / 153);
    },
  },
  methods: {
    ...mapActions('smsTemplate', ['getSMSTemplate', 'createSMSTemplate', 'updateSMSTemplate']),
    insertVariable(v) {
      this.form.body = (this.form.body || '') + v;
    },
    onSelectTemplate(tpl) {
      this.form.name = tpl.name;
      this.form.body = tpl.body;
      this.hasStarted = true;
    },
    onSelectBlank() {
      this.hasStarted = true;
    },
    async loadTemplate() {
      const id = this.$route.params.id;
      if (!id) return;
      this.isEdit = true;
      try {
        const res = await this.getSMSTemplate(id);
        this.form = res.data;
      } catch (err) {
        this.makeToast('danger', 'Failed to load');
      }
    },
    async saveTemplate() {
      this.isSaving = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        this.form.school = user.school.id;
        if (this.isEdit) {
          await this.updateSMSTemplate({ id: this.$route.params.id, data: this.form });
          this.makeToast('success', 'Updated');
        } else {
          await this.createSMSTemplate(this.form);
          this.makeToast('success', 'Created');
          this.$router.push({ name: 'sms-templates-list' });
        }
      } catch (err) {
        this.makeToast('danger', 'Save failed');
      } finally {
        this.isSaving = false;
      }
    },
  },
  created() {
    this.loadTemplate();
  },
};
</script>

<style scoped>
.ste {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 100px);
}

/* Header */
.ste-header {
  background: #fff;
  border-bottom: 1px solid #e2e6ea;
  padding: 14px 24px 16px;
}
.ste-title-row { display: flex; align-items: center; }
.ste-back {
  color: #272C33;
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
}
.ste-title { font-size: 1.25rem; font-weight: 700; margin: 0; }
.ste-fields-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 12px;
  gap: 16px;
  flex-wrap: wrap;
}
.ste-fields { display: flex; gap: 16px; flex: 1; min-width: 240px; }
.ste-field { flex: 1; min-width: 200px; }
.ste-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #858796;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}
.ste-input {
  width: 100%;
  border: 1px solid #d9dde3;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  background: #fff;
}
.ste-input:focus { outline: none; border-color: #4e73df; box-shadow: 0 0 0 2px rgba(78,115,223,0.15); }
.ste-textarea { resize: vertical; min-height: 180px; line-height: 1.4; }

.ste-actions { display: flex; gap: 8px; }
.ste-btn {
  border: 1px solid #d9dde3;
  background: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.ste-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ste-btn--primary { background: #4e73df; border-color: #4e73df; color: #fff; }
.ste-btn--ghost { background: transparent; }

.ste-var-btn {
  display: inline-block;
  font-size: 12px;
  border: 1px solid #d9dde3;
  background: #fff;
  border-radius: 4px;
  padding: 3px 8px;
  margin: 2px 4px 2px 0;
  cursor: pointer;
  color: #272C33;
}
.ste-var-btn:hover { background: #eef3ff; border-color: #4e73df; }

/* Split */
.ste-split {
  flex: 1;
  min-height: 0;
  display: flex;
}
.ste-edit {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 20px 24px;
  background: #f4f3ed;
  border-right: 1px solid #e2e6ea;
}
.ste-form {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e6ea;
  padding: 24px 28px;
}

.ste-preview {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 32px 24px;
  background: #eae9e3;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* Phone mockup */
.phone {
  width: 320px;
  background: #fff;
  border-radius: 36px;
  border: 10px solid #1f1f24;
  box-shadow: 0 24px 50px rgba(15, 15, 25, 0.22);
  overflow: hidden;
  position: relative;
}
.phone-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 110px;
  height: 18px;
  background: #1f1f24;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
  z-index: 2;
}
.phone-header {
  background: #f6f7fb;
  padding: 26px 16px 10px;
  text-align: center;
  border-bottom: 1px solid #ececf2;
}
.phone-from { font-size: 13px; font-weight: 600; color: #20232a; }
.phone-time { font-size: 11px; color: #8a8d96; margin-top: 2px; }
.phone-body {
  padding: 18px 14px;
  min-height: 320px;
  background: #fff;
}
.bubble {
  max-width: 82%;
  padding: 10px 14px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.bubble.inbound {
  background: #e9eaee;
  color: #1d1f24;
  border-bottom-left-radius: 4px;
}
.bubble-empty { font-style: italic; color: #8a8d96; }
.phone-meta {
  border-top: 1px solid #ececf2;
  padding: 10px 16px;
  font-size: 11px;
  color: #8a8d96;
  text-align: center;
  background: #f6f7fb;
}

@media (max-width: 900px) {
  .ste-split { flex-direction: column; }
  .ste-edit { border-right: none; border-bottom: 1px solid #e2e6ea; }
}
</style>
